import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type Dispatch,
} from 'react'

import { Box } from '@mui/material'
import { EditorContent, useEditor } from '@tiptap/react'
import Ai from '@tiptap-pro/extension-ai-advanced'
import type { Assistant, AssistantTool } from 'types/graphql'

import { useAuth } from 'src/auth'
import AssistantTyping from 'src/components/Assistant/AssistantTyping'
import Row from 'src/components/Row/Row'
import type { OpenAIChatMessage } from 'src/components/Threads/ThreadTiptap/utils'
import type { ThreadAction } from 'src/components/Threads/V2/Provider/ThreadContext'
import { logger } from 'src/lib/logger'
import { DEFAULT_MODEL_ID } from 'src/lib/models'
import type { AvailableModelId } from 'src/lib/models'
import type { DocumentObject } from 'src/lib/pages'
import { isConnectionError } from 'src/lib/utils'

import { extensionsForParentObject } from '../extensions'

import { processSSEStream } from './sseProcessor'

const log = false

const logAiEditor = (...args) => {
  if (log) {
    logger.dev('AiEditor', ...args)
  }
}

const thinkingSx = {
  width: '100%',
  height: '32px',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  mt: '-2px',
}

// Model remapping now handled in lambda handler

const forceProd = false

const streamUrl =
  process.env.HOST.includes('localhost') && !forceProd
    ? 'http://localhost:3001/'
    : `https://f5gegaoxtd4kuyhc35q62wdyoq0ciwqh.lambda-url.us-east-1.on.aws/`

const AiEditorComponent = ({
  token,
  prompt,
  onSuccess,
  contextString,
  parentObject,
  modelId,
  thinkingEnabled,
  dispatch,
  messages,
  assistant: _assistant,
  tools,
  instructions,
}: {
  token: string
  prompt?: string
  messages?: OpenAIChatMessage[]
  onSuccess: (result: any) => void
  contextString: string
  parentObject: DocumentObject
  modelId?: AvailableModelId
  thinkingEnabled?: boolean
  dispatch?: Dispatch<ThreadAction>
  tools?: AssistantTool[]
  assistant?: Assistant
  instructions?: string
}) => {
  const { currentUser, getToken } = useAuth()
  const memoizedOnSuccess = useCallback(onSuccess, [onSuccess])
  const memoizedPrompt = useMemo(() => prompt ?? 'Placeholder prompt', [prompt])
  const memoizedContextString = useMemo(() => contextString, [contextString])
  const memoizedToken = useMemo(() => token, [token])
  const memoizedMessages = useMemo(() => messages, [messages])
  const memoizedInstructions = useMemo(() => instructions ?? '', [instructions])
  const memoizedParentObject = useMemo(() => parentObject, [parentObject])

  const memoizedModelId = useMemo(() => {
    return modelId || DEFAULT_MODEL_ID
  }, [modelId])

  const memoizedTools = useMemo(
    () =>
      (tools || []).map((t: AssistantTool) => {
        return {
          type: t.function.type,
          function: {
            parameters: {
              properties: t.function.function.parameters?.properties,
              required: t.function.function.parameters?.required,
              type: t.function.function.parameters?.type,
            },
            name: t.function.function.name,
            description: t.function.function.description,
          },
        }
      }),
    [tools]
  )
  const memoizedToolChoice = useMemo(
    () => ((tools || []).length > 0 ? 'auto' : 'none'),
    [tools]
  )

  const aiInitialized = useRef(false)
  const successCalled = useRef(false)
  const contentPopulated = useRef(false)

  const handleError = useCallback(
    (error: string) => {
      if (dispatch) {
        dispatch({
          type: 'SET_THREAD_ERROR',
          payload: error,
        })
      }
    },
    [dispatch]
  )

  const editor = useEditor({
    immediatelyRender: true,
    shouldRerenderOnTransaction: true,
    extensions: [
      ...extensionsForParentObject({
        parentObject,
      }),
      Ai.configure({
        appId: process.env.TIPTAP_APP_ID,
        token: memoizedToken,
        autocompletion: true,

        onChunk: (_) => {
          if (contentPopulated.current) {
            return
          }
          contentPopulated.current = true
        },

        onSuccess: (result) => {
          if (successCalled.current) {
            logAiEditor('Success already called, skipping')
            return
          }
          successCalled.current = true
          memoizedOnSuccess(result)
        },
        showDecorations: false,
        aiStreamResolver: async ({ action, text, textOptions }) => {
          logAiEditor('aiStreamResolver called', { action, text, textOptions })
          const chatMessages: OpenAIChatMessage[] = [
            {
              role: 'system' as const,
              content: memoizedInstructions,
            },
            {
              role: 'user',
              content:
                'Remember, if you do not respond with only HTML, it will cause an error in the application.',
            },
            {
              role: 'user',
              content: memoizedContextString,
            },
          ]

          if (memoizedMessages) {
            chatMessages.push(...memoizedMessages)
          } else {
            chatMessages.push({
              role: 'user',
              content: memoizedPrompt,
            })
          }

          const authToken = await getToken()

          const fetchOptions = {
            method: 'POST',
            headers: {
              accept: 'text/event-stream',
              'content-type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              ...textOptions,
              // This variable must exist or it will break the TipTap API
              text,
              modelId: memoizedModelId,
              messages: chatMessages,
              thinkingEnabled: thinkingEnabled ?? false,
              tools: memoizedTools,
              toolChoice: memoizedToolChoice,
              userId: currentUser?.id,
              parentObject: memoizedParentObject,
              timezone:
                Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            }),
          }

          const MAX_RETRIES = 3
          const MAX_CONNECTION_RETRIES = 5
          let attempt = 0
          let connectionAttempt = 0

          // Helper function to calculate exponential backoff with jitter
          const calculateBackoffDelay = (attemptNumber: number): number => {
            const baseDelay = Math.pow(2, attemptNumber - 1) * 1000 // 1s, 2s, 4s, 8s, 16s
            const maxDelay = 16000 // Cap at 16 seconds
            const delay = Math.min(baseDelay, maxDelay)
            // Add 10% jitter
            return Math.floor(delay * (0.9 + Math.random() * 0.2))
          }

          while (
            attempt < MAX_RETRIES ||
            connectionAttempt < MAX_CONNECTION_RETRIES
          ) {
            try {
              const response = await fetch(streamUrl, fetchOptions)

              if (!response.ok) {
                const errorText = await response.text()

                // Check if it's a rate limit error
                let errorData
                try {
                  errorData = JSON.parse(errorText)
                } catch {
                  // Not JSON, treat as regular error
                  errorData = null
                }

                // Check for ECONNRESET error from server response
                if (
                  errorData?.code === 'ECONNRESET' &&
                  errorData?.errorType === 'SERVER_ERROR' &&
                  connectionAttempt < MAX_CONNECTION_RETRIES
                ) {
                  connectionAttempt++
                  const delayMs = calculateBackoffDelay(connectionAttempt)
                  const delaySeconds = Math.ceil(delayMs / 1000)

                  if (dispatch) {
                    // Show initial message
                    dispatch({
                      type: 'SET_LOADING_MESSAGE',
                      payload: {
                        label: `Network connection error. Retrying in ${delaySeconds} seconds... (Attempt ${connectionAttempt} of ${MAX_CONNECTION_RETRIES})`,
                      },
                    })
                  }

                  logger.info('ECONNRESET error detected, retrying...', {
                    connectionAttempt,
                    delayMs,
                    errorData,
                  })

                  // Countdown with real-time updates
                  const startTime = Date.now()
                  const endTime = startTime + delayMs

                  while (Date.now() < endTime) {
                    const remainingMs = endTime - Date.now()
                    const remainingSeconds = Math.ceil(remainingMs / 1000)

                    if (dispatch && remainingSeconds > 0) {
                      dispatch({
                        type: 'SET_LOADING_MESSAGE',
                        payload: {
                          label: `Network connection error. Retrying in ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}... (Attempt ${connectionAttempt} of ${MAX_CONNECTION_RETRIES})`,
                        },
                      })
                    }

                    // Wait 1 second or until the end time, whichever is shorter
                    const waitTime = Math.min(1000, remainingMs)
                    if (waitTime > 0) {
                      await new Promise((resolve) =>
                        setTimeout(resolve, waitTime)
                      )
                    } else {
                      break
                    }
                  }
                  continue
                }

                if (
                  errorData?.Reason === 'ConcurrentInvocationLimitExceeded' &&
                  errorData?.Type === 'User' &&
                  errorData?.message === 'Rate Exceeded.' &&
                  attempt < MAX_RETRIES - 1
                ) {
                  // Inform user about retry
                  if (dispatch) {
                    dispatch({
                      type: 'SET_THREAD_STATUS',
                      payload: 'Rate limit reached. Retrying in 3 seconds...',
                    })
                  }
                  logger.info('Rate limit exceeded, retrying in 3 seconds...', {
                    attempt,
                  })
                  await new Promise((resolve) => setTimeout(resolve, 3000))
                  attempt++
                  continue
                }

                handleError(errorText)
                throw new Error(
                  `HTTP error! status: ${response.status}, message: ${errorText}`
                )
              }

              if (!response.body) {
                handleError('Response body is null')
                throw new Error('Response body is null')
              }

              return processSSEStream(response.body, dispatch)
            } catch (error) {
              // Check if it's a connection error
              if (
                isConnectionError(error) &&
                connectionAttempt < MAX_CONNECTION_RETRIES
              ) {
                connectionAttempt++
                const delayMs = calculateBackoffDelay(connectionAttempt)
                const delaySeconds = Math.ceil(delayMs / 1000)

                if (dispatch) {
                  // Show initial message
                  dispatch({
                    type: 'SET_LOADING_MESSAGE',
                    payload: {
                      label: `Network connection error. Retrying in ${delaySeconds} seconds... (Attempt ${connectionAttempt} of ${MAX_CONNECTION_RETRIES})`,
                    },
                  })
                }

                logger.info('Connection error detected, retrying...', {
                  connectionAttempt,
                  delayMs,
                  error: error instanceof Error ? error.message : String(error),
                  errorCode: (error as any)?.code,
                })

                // Countdown with real-time updates
                const startTime = Date.now()
                const endTime = startTime + delayMs

                while (Date.now() < endTime) {
                  const remainingMs = endTime - Date.now()
                  const remainingSeconds = Math.ceil(remainingMs / 1000)

                  if (dispatch && remainingSeconds > 0) {
                    dispatch({
                      type: 'SET_LOADING_MESSAGE',
                      payload: {
                        label: `Network connection error. Retrying in ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}... (Attempt ${connectionAttempt} of ${MAX_CONNECTION_RETRIES})`,
                      },
                    })
                  }

                  // Wait 1 second or until the end time, whichever is shorter
                  const waitTime = Math.min(1000, remainingMs)
                  if (waitTime > 0) {
                    await new Promise((resolve) =>
                      setTimeout(resolve, waitTime)
                    )
                  } else {
                    break
                  }
                }
                continue
              }

              // Check if it's a "Failed to fetch" error in development
              const isDev = process.env.HOST.includes('localhost')
              const isFailedToFetch =
                error instanceof Error && error.message === 'Failed to fetch'

              if (isDev && isFailedToFetch && dispatch) {
                // Add special system message for development server
                dispatch({
                  type: 'ADD_SYSTEM_MESSAGE',
                  payload: {
                    message:
                      'Hey there, please run <br><br><code>node api/src/streaming/lambda-dev-server.mjs</code><br><br> to start your development server.',
                  },
                })
              }

              // If we've exhausted retries
              const isConnectionExhausted =
                isConnectionError(error) &&
                connectionAttempt >= MAX_CONNECTION_RETRIES
              const isRateLimitExhausted = attempt >= MAX_RETRIES - 1

              if (isConnectionExhausted || isRateLimitExhausted) {
                logger.error(
                  'Error in aiStreamResolver fetching or processing stream:',
                  error
                )
                if (dispatch) {
                  let errorMessage = 'Failed to stream AI response.'

                  if (isConnectionExhausted) {
                    errorMessage = `We're having trouble connecting to our servers. This might be due to your internet connection or temporary server issues. Please check your connection and try again.`
                  } else if (isDev && isFailedToFetch) {
                    errorMessage =
                      'Failed to connect to development server. Please check the system message above.'
                  }

                  dispatch({
                    type: 'SET_THREAD_ERROR',
                    payload: errorMessage,
                  })
                }
                return new ReadableStream({
                  start(controller) {
                    controller.close()
                  },
                })
              }
              throw error // Re-throw to be caught by while loop
            }
          }
        },
      }),
    ],
    autofocus: false,
    editable: false,
  })

  useEffect(() => {
    const initializeAi = async () => {
      if (!editor || !memoizedPrompt) {
        logAiEditor('Editor or prompt not found, skipping AI initialization')
        return
      }

      successCalled.current = false

      try {
        logAiEditor('Starting AI population', {
          token: memoizedToken,
          prompt: memoizedPrompt,
          contextString: memoizedContextString,
        })
        if (dispatch) {
          dispatch({
            type: 'SET_AI_INITIALIZED_AT',
            payload: { timestamp: new Date().getTime() },
          })
        }

        editor
          .chain()
          .aiTextPrompt({
            // These variables must exist or it will break the TipTap API
            text: `Placeholder text`,
            stream: true,
            insert: true,
            format: 'rich-text',
            context: [
              {
                type: 'text',
                text: `Placeholder text`,
              },
            ],
          })
          .run()
      } catch (error) {
        logger.error('AI initialization failed:', error)
        if (dispatch) {
          dispatch({
            type: 'SET_THREAD_ERROR',
            payload: 'AI initialization failed',
          })
        }
      }
    }

    if (
      !aiInitialized.current &&
      editor &&
      memoizedPrompt &&
      memoizedToken &&
      memoizedContextString
    ) {
      aiInitialized.current = true
      initializeAi()
    }
  }, [
    editor,
    memoizedPrompt,
    memoizedContextString,
    memoizedToken,
    modelId,
    memoizedMessages,
    dispatch,
  ])

  const containerSx = {
    width: '100%',
  }

  logAiEditor('AiEditorComponent', {
    token: memoizedToken,
    prompt: memoizedPrompt,
    contextString: memoizedContextString,
  })

  return (
    <Box sx={containerSx}>
      {!contentPopulated.current && (
        <Row sx={thinkingSx}>
          <AssistantTyping />
        </Row>
      )}
      <div className="editor-tiptap-inner">
        <EditorContent editor={editor} />
      </div>
    </Box>
  )
}

const AiEditor = memo(AiEditorComponent, (previous, next) => {
  return (
    previous.token === next.token &&
    previous.prompt === next.prompt &&
    previous.modelId === next.modelId &&
    previous.thinkingEnabled === next.thinkingEnabled &&
    previous.messages === next.messages &&
    previous.tools === next.tools &&
    previous.instructions === next.instructions
  )
})

AiEditor.displayName = 'AiEditor'

export default AiEditor
